<template>
  <main class="admin-shell">
    <aside class="admin-sidebar">
      <div class="admin-brand">Homepage Admin</div>
      <button
        v-for="item in sections"
        :key="item.key"
        :class="{ active: active === item.key }"
        type="button"
        @click="switchSection(item.key)"
      >
        {{ item.label }}
      </button>
    </aside>

    <section class="admin-main">
      <header class="admin-topbar">
        <div>
          <h1>{{ currentTitle }}</h1>
          <span>{{ statusText }}</span>
        </div>
        <div class="admin-actions">
          <a href="/" target="_blank">查看前台</a>
          <button type="button" @click="logout">退出</button>
        </div>
      </header>

      <section v-if="active === 'dashboard'" class="admin-grid">
        <article v-for="(value, key) in dashboard" :key="key" class="admin-stat">
          <span>{{ statLabel(key) }}</span>
          <strong>{{ value }}</strong>
        </article>
      </section>

      <section v-if="active === 'home'" class="admin-panel">
        <div class="admin-form-grid">
          <label v-for="field in homeFields" :key="field.key">
            {{ field.label }}
            <input v-model="homeConfig[field.key]">
          </label>
        </div>
        <button class="primary" type="button" @click="saveHome">保存首页配置</button>
      </section>

      <section v-if="active === 'weather'" class="admin-panel">
        <div class="admin-form-grid">
          <label v-for="field in weatherFields" :key="field.key">
            {{ field.label }}
            <input v-model="weather[field.key]">
          </label>
        </div>
        <button class="primary" type="button" @click="saveWeather">保存天气配置</button>
      </section>

      <section v-if="active === 'musicConfig'" class="admin-panel">
        <div class="admin-form-grid">
          <label v-for="field in musicConfigFields" :key="field.key">
            {{ field.label }}
            <select v-if="field.options" v-model="musicConfig[field.key]">
              <option v-for="option in field.options" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
            <input v-else-if="field.type === 'number'" v-model.number="musicConfig[field.key]" type="number">
            <input v-else v-model="musicConfig[field.key]">
          </label>
        </div>
        <button class="primary" type="button" @click="saveMusicConfig">保存音乐配置</button>
      </section>

      <section v-if="active === 'hotSources'" class="admin-panel">
        <div class="admin-hot-actions">
          <p>系统会按各数据源的刷新间隔自动更新；抓取失败时保留上一次成功数据。</p>
          <button class="primary" type="button" :disabled="hotRefreshing" @click="refreshAllHot">
            {{ hotRefreshing ? '刷新中…' : '立即刷新全部' }}
          </button>
          <button type="button" :disabled="hotRefreshing" @click="createHotSnapshot">
            分析当前热榜
          </button>
        </div>
        <div class="admin-hot-list">
          <article v-for="source in hotSources" :key="source.id" class="admin-hot-card">
            <header>
              <strong>{{ source.name }}</strong>
              <span :class="['admin-hot-state', source.lastStatus]">
                {{ source.lastStatus === 'success' ? '正常' : (source.lastStatus === 'failed' ? '失败' : '未刷新') }}
              </span>
            </header>
            <label>名称<input v-model.trim="source.name"></label>
            <label>来源代码<input v-model.trim="source.code" disabled></label>
            <label>接口地址<input v-model.trim="source.apiUrl"></label>
            <div class="admin-hot-fields">
              <label>启用
                <select v-model.number="source.enabled">
                  <option :value="1">启用</option>
                  <option :value="0">停用</option>
                </select>
              </label>
              <label>刷新（分钟）<input v-model.number="source.refreshMinutes" type="number" min="5"></label>
              <label>排序<input v-model.number="source.sort" type="number"></label>
            </div>
            <p class="admin-hot-message">{{ source.lastMessage || '等待首次刷新' }}</p>
            <small>最近成功：{{ source.lastSuccessTime ? source.lastSuccessTime.replace('T', ' ') : '暂无' }}</small>
            <footer>
              <button type="button" @click="saveHotSource(source)">保存</button>
              <button class="muted" type="button" :disabled="hotRefreshing" @click="refreshHotSource(source)">
                刷新此来源
              </button>
            </footer>
          </article>
        </div>
      </section>

      <section v-if="isPhotoSection" class="admin-panel">
        <div class="admin-photo-toolbar">
          <input v-model.trim="photoQuery.keyword" placeholder="搜索照片">
          <select v-model="photoQuery.albumId" @change="loadPhotos">
            <option value="">全部相册</option>
            <option v-for="album in photoAlbums" :key="album.id" :value="album.id">{{ album.name }}</option>
          </select>
          <select v-model="photoQuery.tagId" @change="loadPhotos">
            <option value="">全部标签</option>
            <option v-for="tag in photoTags" :key="tag.id" :value="tag.id">{{ tag.name }}</option>
          </select>
          <select v-model.number="photoSize" @change="loadPhotos">
            <option :value="12">12 条/页</option>
            <option :value="20">20 条/页</option>
            <option :value="30">30 条/页</option>
          </select>
          <button type="button" @click="searchPhotos">查询</button>
        </div>

        <div v-if="active === 'photoLibrary'" class="admin-photo-editor">
          <input v-model.trim="photoDraft.title" placeholder="照片标题">
          <select v-model="photoDraft.albumId">
            <option value="">选择相册</option>
            <option v-for="album in photoAlbums" :key="album.id" :value="album.id">{{ album.name }}</option>
          </select>
          <input v-model.number="photoDraft.sort" type="number" placeholder="排序">
          <input v-model.trim="photoDraft.imageUrl" placeholder="图片URL">
          <input v-model.trim="photoDraft.uploadFileName" placeholder="上传文件名，如 summer-01">
          <input accept="image/*" type="file" @change="uploadPhoto($event, photoDraft)">
          <textarea v-model.trim="photoDraft.description" placeholder="照片描述"></textarea>
          <div class="admin-photo-tags">
            <label v-for="tag in photoTags" :key="tag.id">
              <input v-model="photoDraft.tagIds" type="checkbox" :value="tag.id">
              {{ tag.name }}
            </label>
          </div>
          <button type="button" @click="addPhoto">新增照片</button>
        </div>

        <div class="admin-photo-list">
          <article v-for="record in photoRows" :key="record.photo.id" class="admin-photo-card">
            <button class="admin-photo-preview-trigger" type="button" @click="openPhotoPreview(record)">
              <img :src="record.photo.thumbnailUrl || record.photo.imageUrl" :alt="record.photo.title || record.photo.originalName">
            </button>
            <div class="admin-photo-card-body">
              <input v-model.trim="record.photo.title" placeholder="标题">
              <select v-model="record.photo.albumId">
                <option value="">未分组</option>
                <option v-for="album in photoAlbums" :key="album.id" :value="album.id">{{ album.name }}</option>
              </select>
              <input v-model.trim="record.photo.imageUrl" placeholder="图片URL">
              <input v-model.trim="record.photo.uploadFileName" placeholder="上传文件名">
              <input accept="image/*" type="file" @change="uploadPhoto($event, record.photo)">
              <textarea v-model.trim="record.photo.description" placeholder="描述"></textarea>
              <div class="admin-photo-tags">
                <label v-for="tag in photoTags" :key="tag.id">
                  <input v-model="record.editTags" type="checkbox" :value="tag.id">
                  {{ tag.name }}
                </label>
              </div>
              <div class="admin-photo-card-actions">
                <button type="button" @click="savePhoto(record)">保存</button>
                <button class="muted" type="button" @click="toggleFavorite(record)">
                  {{ record.photo.isFavorite === 1 ? '取消收藏' : '收藏' }}
                </button>
                <button v-if="active === 'photoTrash'" type="button" @click="restorePhoto(record)">恢复</button>
                <button v-else class="danger" type="button" @click="deletePhoto(record)">删除</button>
              </div>
            </div>
          </article>
        </div>
        <footer class="photo-pager">
          <button type="button" :disabled="photoPage <= 1" @click="changePhotoPage(photoPage - 1)">‹</button>
          <span>{{ photoPage }}</span>
          <button type="button" :disabled="photoPage >= photoTotalPages" @click="changePhotoPage(photoPage + 1)">›</button>
        </footer>
      </section>

      <div v-if="photoPreview" class="image-preview-overlay" @click="closePhotoPreview">
        <section class="image-preview-dialog" @click.stop>
          <button class="image-preview-close" type="button" aria-label="关闭预览" @click="closePhotoPreview">×</button>
          <img :src="photoPreview.photo.imageUrl || photoPreview.photo.thumbnailUrl" :alt="photoPreviewTitle">
          <div class="image-preview-info">
            <strong>{{ photoPreviewTitle }}</strong>
            <span>{{ photoPreview.albumName || '默认相册' }}</span>
            <div v-if="photoPreview.tags && photoPreview.tags.length" class="image-preview-tags">
              <em v-for="tag in photoPreview.tags" :key="tag.id">{{ tag.name }}</em>
            </div>
          </div>
        </section>
      </div>

      <section v-if="listConfig" class="admin-panel">
        <div class="admin-inline-form">
          <div v-for="field in listConfig.fields" :key="field.key" class="admin-field-cell">
            <select v-if="field.type === 'category'" v-model.number="draft[field.key]">
              <option disabled value="">选择分类</option>
              <option v-for="category in bookmarkCategories" :key="category.id" :value="category.id">
                {{ category.name }}
              </option>
            </select>
            <input v-else v-model="draft[field.key]" :placeholder="field.label">
            <template v-if="isMusicAudioField(field)">
              <input v-model.trim="draft.uploadFileName" placeholder="上传文件名，如 my-song">
              <input accept="audio/*" type="file" @change="uploadMusic($event, draft)">
            </template>
          </div>
          <button class="primary" type="button" @click="addItem">新增</button>
        </div>

        <div class="admin-table-wrap">
          <table class="admin-table">
            <thead>
              <tr>
                <th v-for="field in listConfig.fields" :key="field.key">{{ field.label }}</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in rows" :key="row.id">
                <td v-for="field in listConfig.fields" :key="field.key">
                  <select v-if="field.type === 'category'" v-model.number="row[field.key]">
                    <option v-for="category in bookmarkCategories" :key="category.id" :value="category.id">
                      {{ category.name }}
                    </option>
                  </select>
                  <input v-else v-model="row[field.key]">
                  <template v-if="isMusicAudioField(field)">
                    <input v-model.trim="row.uploadFileName" placeholder="上传文件名，如 my-song">
                    <input accept="audio/*" type="file" @change="uploadMusic($event, row)">
                  </template>
                </td>
                <td class="row-actions">
                  <button type="button" @click="saveItem(row)">保存</button>
                  <button type="button" @click="removeItem(row)">删除</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </section>
  </main>
</template>

<script>
import { deleteAdmin, getAdmin, postAdmin, putAdmin, uploadAdmin } from '../api/admin';

const listConfigs = {
  photoAlbums: {
    title: '相册管理',
    path: '/photo-albums',
    fields: [
      { key: 'name', label: '名称' },
      { key: 'description', label: '描述' },
      { key: 'coverUrl', label: '封面URL' },
      { key: 'sort', label: '排序' },
      { key: 'status', label: '状态' }
    ]
  },
  photoTags: {
    title: '标签管理',
    path: '/photo-tags',
    fields: [
      { key: 'name', label: '名称' },
      { key: 'color', label: '颜色' },
      { key: 'sort', label: '排序' },
      { key: 'status', label: '状态' }
    ]
  },
  quotes: {
    title: '一句话管理',
    path: '/quotes',
    fields: [
      { key: 'content', label: '内容' },
      { key: 'source', label: '来源' },
      { key: 'author', label: '作者' },
      { key: 'type', label: '类型' },
      { key: 'status', label: '状态' }
    ]
  },
  musicTracks: {
    title: '音乐列表',
    path: '/music/tracks',
    fields: [
      { key: 'title', label: '歌名' },
      { key: 'artist', label: '歌手' },
      { key: 'coverUrl', label: '封面URL' },
      { key: 'audioUrl', label: '音频URL' },
      { key: 'sort', label: '排序' },
      { key: 'status', label: '状态' }
    ]
  },
  siteLinks: {
    title: '网站入口',
    path: '/site-links',
    fields: [
      { key: 'title', label: '标题' },
      { key: 'description', label: '描述' },
      { key: 'icon', label: '图标' },
      { key: 'url', label: '链接' },
      { key: 'target', label: '打开方式' },
      { key: 'sort', label: '排序' },
      { key: 'status', label: '状态' }
    ]
  },
  bookmarkCategories: {
    title: '网址分类',
    path: '/bookmark-categories',
    fields: [
      { key: 'name', label: '分类名称' },
      { key: 'sort', label: '排序' },
      { key: 'status', label: '状态' }
    ]
  },
  bookmarkLinks: {
    title: '网址管理',
    path: '/bookmark-links',
    fields: [
      { key: 'categoryId', label: '所属分类', type: 'category' },
      { key: 'title', label: '网站名称' },
      { key: 'url', label: '网址' },
      { key: 'description', label: '描述' },
      { key: 'sort', label: '排序' },
      { key: 'status', label: '状态' }
    ]
  },
  socialLinks: {
    title: '社交链接',
    path: '/social-links',
    fields: [
      { key: 'name', label: '名称' },
      { key: 'icon', label: '图标' },
      { key: 'url', label: '链接' },
      { key: 'sort', label: '排序' },
      { key: 'status', label: '状态' }
    ]
  },
  navMenus: {
    title: '导航菜单',
    path: '/nav-menus',
    fields: [
      { key: 'name', label: '名称' },
      { key: 'icon', label: '图标' },
      { key: 'path', label: '路径' },
      { key: 'target', label: '打开方式' },
      { key: 'sort', label: '排序' },
      { key: 'status', label: '状态' }
    ]
  }
};

export default {
  name: 'Admin',
  data() {
    return {
      active: 'dashboard',
      statusText: '',
      dashboard: {},
      rows: [],
      draft: {},
      photoRows: [],
      photoAlbums: [],
      photoTags: [],
      photoQuery: {
        keyword: '',
        albumId: '',
        tagId: ''
      },
      photoDraft: {
        title: '',
        description: '',
        albumId: '',
        imageUrl: '',
        thumbnailUrl: '',
        uploadFileName: '',
        sort: 10,
        status: 1,
        tagIds: []
      },
      photoPage: 1,
      photoSize: 12,
      photoTotal: 0,
      photoPreview: null,
      homeConfig: {},
      weather: {},
      musicConfig: {},
      hotSources: [],
      hotRefreshing: false,
      bookmarkCategories: [],
      sections: [
        { key: 'dashboard', label: '控制台' },
        { key: 'anime', label: '动画管理' },
        { key: 'home', label: '首页配置' },
        { key: 'photoLibrary', label: '照片库' },
        { key: 'photoAlbums', label: '相册管理' },
        { key: 'photoTags', label: '标签管理' },
        { key: 'photoTrash', label: '回收站' },
        { key: 'quotes', label: '一句话' },
        { key: 'musicConfig', label: '音乐配置' },
        { key: 'musicTracks', label: '音乐列表' },
        { key: 'hotSources', label: '热榜管理' },
        { key: 'siteLinks', label: '网站入口' },
        { key: 'bookmarkCategories', label: '网址分类' },
        { key: 'bookmarkLinks', label: '网址管理' },
        { key: 'socialLinks', label: '社交链接' },
        { key: 'navMenus', label: '导航菜单' },
        { key: 'weather', label: '天气配置' }
      ],
      homeFields: [
        { key: 'siteName', label: '站点名称' },
        { key: 'logoText', label: 'Logo文字' },
        { key: 'avatarUrl', label: '头像URL' },
        { key: 'backgroundUrl', label: '背景URL' },
        { key: 'helloText', label: '欢迎语' },
        { key: 'mainTitle', label: '主标题' },
        { key: 'highlightText', label: '高亮文案' },
        { key: 'icpText', label: '备案号' },
        { key: 'copyrightText', label: '版权信息' },
        { key: 'status', label: '状态' }
      ],
      weatherFields: [
        { key: 'cityName', label: '城市' },
        { key: 'temperature', label: '温度' },
        { key: 'weatherText', label: '天气' },
        { key: 'airQuality', label: '空气质量' },
        { key: 'tips', label: '提示语' },
        { key: 'status', label: '状态' }
      ],
      musicConfigFields: [
        { key: 'enabled', label: '启用', options: [{ value: 1, label: '启用' }, { value: 0, label: '禁用' }] },
        { key: 'autoplay', label: '自动播放', options: [{ value: 0, label: '否' }, { value: 1, label: '是' }] },
        { key: 'mode', label: '播放模式', options: [{ value: 'meting', label: '网易云歌单' }, { value: 'local', label: '本地列表' }] },
        { key: 'metingApi', label: 'Meting API' },
        { key: 'metingId', label: '歌单ID' },
        { key: 'metingServer', label: '音乐平台', options: [{ value: 'netease', label: '网易云音乐' }, { value: 'tencent', label: 'QQ音乐' }, { value: 'kugou', label: '酷狗音乐' }, { value: 'baidu', label: '百度音乐' }] },
        { key: 'metingType', label: '歌单类型', options: [{ value: 'playlist', label: '歌单' }, { value: 'song', label: '单曲' }, { value: 'album', label: '专辑' }, { value: 'artist', label: '歌手' }] },
        { key: 'loopMode', label: '循环模式', options: [{ value: 'list', label: '列表' }, { value: 'single', label: '单曲' }, { value: 'random', label: '随机' }] },
        { key: 'volume', label: '音量', type: 'number' },
        { key: 'playerPosition', label: '位置', options: [{ value: 'right-bottom', label: '右下' }, { value: 'left-bottom', label: '左下' }] },
        { key: 'showLyric', label: '歌词', options: [{ value: 0, label: '隐藏' }, { value: 1, label: '显示' }] },
        { key: 'status', label: '状态', options: [{ value: 1, label: '启用' }, { value: 0, label: '禁用' }] }
      ]
    };
  },
  computed: {
    listConfig() {
      return listConfigs[this.active];
    },
    currentTitle() {
      if (this.listConfig) {
        return this.listConfig.title;
      }
      const found = this.sections.find(item => item.key === this.active);
      return found ? found.label : '后台管理';
    },
    isPhotoSection() {
      return this.active === 'photoLibrary' || this.active === 'photoTrash';
    },
    photoTotalPages() {
      return Math.max(1, Math.ceil((this.photoTotal || 0) / this.photoSize));
    },
    photoPreviewTitle() {
      if (!this.photoPreview || !this.photoPreview.photo) {
        return '照片预览';
      }
      return this.photoPreview.photo.title || this.photoPreview.photo.originalName || '未命名照片';
    }
  },
  created() {
    this.loadCurrent();
  },
  mounted() {
    window.addEventListener('keydown', this.handlePhotoPreviewKeydown);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handlePhotoPreviewKeydown);
  },
  methods: {
    switchSection(key) {
      if (key === 'anime') {
        this.$router.push('/admin/anime');
        return;
      }
      this.active = key;
      this.rows = [];
      this.draft = {};
      this.photoRows = [];
      this.photoPage = 1;
      this.loadCurrent();
    },
    loadCurrent() {
      this.statusText = '加载中...';
      const tasks = {
        dashboard: () => getAdmin('/dashboard').then(data => { this.dashboard = data; }),
        home: () => getAdmin('/home-config').then(data => { this.homeConfig = data; }),
        weather: () => getAdmin('/weather').then(data => { this.weather = data; }),
        musicConfig: () => getAdmin('/music/config').then(data => { this.musicConfig = this.normalizeMusicConfig(data); }),
        hotSources: () => this.loadHotSources(),
        photoLibrary: () => this.loadPhotoMeta().then(() => this.loadPhotos()),
        photoTrash: () => this.loadPhotoMeta().then(() => this.loadPhotos())
      };
      const task = this.active === 'bookmarkLinks'
        ? () => Promise.all([
          getAdmin('/bookmark-categories'),
          getAdmin('/bookmark-links')
        ]).then(([categories, links]) => {
          this.bookmarkCategories = categories || [];
          this.rows = links || [];
          this.resetDraft();
        })
        : (this.listConfig
          ? () => getAdmin(this.listConfig.path).then(data => {
            this.rows = data;
            this.resetDraft();
          })
          : tasks[this.active]);
      if (!task) {
        return;
      }
      task()
        .then(() => { this.statusText = '已同步'; })
        .catch(() => { this.statusText = '加载失败，请重新登录'; });
    },
    saveHome() {
      putAdmin('/home-config', this.homeConfig).then(data => {
        this.homeConfig = data;
        this.statusText = '首页配置已保存';
      });
    },
    saveWeather() {
      putAdmin('/weather', this.weather).then(data => {
        this.weather = data;
        this.statusText = '天气配置已保存';
      });
    },
    saveMusicConfig() {
      putAdmin('/music/config', this.normalizeMusicConfig(this.musicConfig)).then(data => {
        this.musicConfig = this.normalizeMusicConfig(data);
        this.statusText = '音乐配置已保存';
      });
    },
    normalizeMusicConfig(data) {
      return {
        enabled: 1,
        autoplay: 0,
        mode: 'meting',
        metingApi: 'https://meting.mysqil.com/api?server=:server&type=:type&id=:id&auth=:auth&r=:r',
        metingId: '492088243',
        metingServer: 'netease',
        metingType: 'playlist',
        loopMode: 'list',
        volume: 60,
        playerPosition: 'right-bottom',
        showLyric: 0,
        status: 1,
        ...(data || {})
      };
    },
    loadHotSources() {
      return getAdmin('/hot/sources').then(data => {
        this.hotSources = data || [];
      });
    },
    saveHotSource(source) {
      putAdmin(`/hot/sources/${source.id}`, source).then(() => {
        this.statusText = `${source.name}已保存`;
        this.loadHotSources();
      }).catch(() => {
        this.statusText = `${source.name}保存失败`;
      });
    },
    refreshAllHot() {
      this.hotRefreshing = true;
      this.statusText = '正在刷新全部热榜...';
      postAdmin('/hot/refresh', {})
        .then(result => {
          this.statusText = `刷新完成：成功 ${result.success || 0}，失败 ${result.failed || 0}`;
          return this.loadHotSources();
        })
        .catch(() => {
          this.statusText = '热榜刷新失败，请检查后端日志';
        })
        .finally(() => {
          this.hotRefreshing = false;
        });
    },
    refreshHotSource(source) {
      this.hotRefreshing = true;
      this.statusText = `正在刷新${source.name}...`;
      postAdmin(`/hot/refresh/${source.code}`, {})
        .then(success => {
          this.statusText = success ? `${source.name}刷新成功` : `${source.name}刷新失败`;
          return this.loadHotSources();
        })
        .catch(() => {
          this.statusText = `${source.name}刷新失败`;
        })
        .finally(() => {
          this.hotRefreshing = false;
        });
    },
    createHotSnapshot() {
      this.hotRefreshing = true;
      this.statusText = '正在基于当前热榜生成快照并调用 DeepSeek 分析...';
      postAdmin('/hot/current-analysis', {})
        .then(result => {
          this.statusText = `当前热榜分析已生成：${result.itemCount || 0} 条，分析状态 ${result.analysisStatus || 'pending'}`;
        })
        .catch(() => {
          this.statusText = '当前热榜分析失败，请检查 DeepSeek 配置或后端日志';
        })
        .finally(() => {
          this.hotRefreshing = false;
        });
    },
    resetDraft() {
      const draft = {};
      this.listConfig.fields.forEach(field => {
        draft[field.key] = field.key === 'status' ? 1 : '';
      });
      if (this.active === 'musicTracks') {
        draft.sort = 10;
        draft.uploadFileName = '';
      }
      if (this.active === 'photoAlbums' || this.active === 'photoTags') {
        draft.sort = 10;
        draft.status = 1;
      }
      if (this.active === 'bookmarkLinks' && this.bookmarkCategories.length) {
        draft.categoryId = this.bookmarkCategories[0].id;
      }
      this.draft = draft;
    },
    addItem() {
      const payload = { ...this.draft };
      delete payload.uploadFileName;
      postAdmin(this.listConfig.path, payload).then(() => {
        this.statusText = '新增成功';
        this.loadCurrent();
      }).catch(error => {
        this.statusText = this.adminErrorMessage(error, '新增失败');
      });
    },
    saveItem(row) {
      const payload = { ...row };
      delete payload.uploadFileName;
      putAdmin(`${this.listConfig.path}/${row.id}`, payload).then(() => {
        this.statusText = '保存成功';
      }).catch(error => {
        this.statusText = this.adminErrorMessage(error, '保存失败');
      });
    },
    removeItem(row) {
      deleteAdmin(`${this.listConfig.path}/${row.id}`).then(() => {
        this.statusText = '删除成功';
        this.loadCurrent();
      }).catch(error => {
        this.statusText = this.adminErrorMessage(error, '删除失败');
      });
    },
    uploadMusic(event, target) {
      const file = event.target.files && event.target.files[0];
      if (!file) {
        return;
      }
      this.statusText = '音乐上传中...';
      uploadAdmin('/upload/music', file, target.uploadFileName)
        .then(data => {
          target.audioUrl = data.url;
          if (!target.title) {
            target.title = data.fileName.replace(/\.[^.]+$/, '');
          }
          this.statusText = target.uploadFileName
            ? `音乐已上传到 music/${target.uploadFileName}`
            : '音乐上传成功';
          event.target.value = '';
        })
        .catch(() => {
          this.statusText = '音乐上传失败，请检查 OSS 配置';
        });
    },
    loadPhotoMeta() {
      return Promise.all([
        getAdmin('/photo-albums'),
        getAdmin('/photo-tags')
      ]).then(([albums, tags]) => {
        this.photoAlbums = albums || [];
        this.photoTags = tags || [];
      });
    },
    loadPhotos() {
      this.statusText = '照片加载中...';
      return getAdmin('/photos', {
        page: this.photoPage,
        size: this.photoSize,
        deleted: this.active === 'photoTrash' ? 1 : 0,
        albumId: this.photoQuery.albumId || undefined,
        tagId: this.photoQuery.tagId || undefined,
        keyword: this.photoQuery.keyword || undefined
      }).then(data => {
        this.photoTotal = data.total || 0;
        this.photoRows = (data.records || []).map(record => ({
          ...record,
          editTags: (record.tags || []).map(tag => tag.id)
        }));
        this.statusText = '照片已同步';
      }).catch(() => {
        this.statusText = '照片加载失败';
      });
    },
    searchPhotos() {
      this.photoPage = 1;
      this.loadPhotos();
    },
    changePhotoPage(page) {
      this.photoPage = page;
      this.loadPhotos();
    },
    resetPhotoDraft() {
      this.photoDraft = {
        title: '',
        description: '',
        albumId: '',
        imageUrl: '',
        thumbnailUrl: '',
        uploadFileName: '',
        sort: 10,
        status: 1,
        tagIds: []
      };
    },
    addPhoto() {
      const payload = this.buildPhotoPayload(this.photoDraft, this.photoDraft.tagIds);
      postAdmin('/photos', payload).then(() => {
        this.statusText = '照片已新增';
        this.resetPhotoDraft();
        this.loadPhotos();
      });
    },
    savePhoto(record) {
      const payload = this.buildPhotoPayload(record.photo, record.editTags);
      putAdmin(`/photos/${record.photo.id}`, payload).then(() => {
        this.statusText = '照片已保存';
        this.loadPhotos();
      });
    },
    deletePhoto(record) {
      deleteAdmin(`/photos/${record.photo.id}`).then(() => {
        this.statusText = '照片已移入回收站';
        this.loadPhotos();
      });
    },
    restorePhoto(record) {
      postAdmin(`/photos/${record.photo.id}/restore`, {}).then(() => {
        this.statusText = '照片已恢复';
        this.loadPhotos();
      });
    },
    toggleFavorite(record) {
      const value = record.photo.isFavorite === 1 ? 0 : 1;
      putAdmin(`/photos/${record.photo.id}/favorite?value=${value}`, {}).then(() => {
        record.photo.isFavorite = value;
        this.statusText = value === 1 ? '已收藏' : '已取消收藏';
      });
    },
    uploadPhoto(event, target) {
      const file = event.target.files && event.target.files[0];
      if (!file) {
        return;
      }
      this.statusText = '照片上传中...';
      uploadAdmin('/upload/photo', file, target.uploadFileName)
        .then(data => {
          target.imageUrl = data.url;
          target.thumbnailUrl = data.url;
          target.originalName = data.fileName;
          target.fileType = this.extractFileType(data.fileName);
          if (!target.title) {
            target.title = data.fileName.replace(/\.[^.]+$/, '');
          }
          this.statusText = target.uploadFileName
            ? `照片已上传到 photos/${target.uploadFileName}`
            : '照片上传成功';
          event.target.value = '';
        })
        .catch(() => {
          this.statusText = '照片上传失败，请检查 OSS 配置';
        });
    },
    openPhotoPreview(record) {
      this.photoPreview = record;
    },
    closePhotoPreview() {
      this.photoPreview = null;
    },
    handlePhotoPreviewKeydown(event) {
      if (event.key === 'Escape' && this.photoPreview) {
        this.closePhotoPreview();
      }
    },
    buildPhotoPayload(source, tagIds) {
      return {
        albumId: source.albumId || null,
        title: source.title || '',
        description: source.description || '',
        imageUrl: source.imageUrl || '',
        thumbnailUrl: source.thumbnailUrl || source.imageUrl || '',
        originalName: source.originalName || '',
        fileType: source.fileType || '',
        sort: source.sort || 10,
        status: source.status == null ? 1 : source.status,
        isFavorite: source.isFavorite || 0,
        tagIds: tagIds || []
      };
    },
    extractFileType(name) {
      if (!name || name.indexOf('.') === -1) {
        return 'IMG';
      }
      return name.split('.').pop().toUpperCase();
    },
    isMusicAudioField(field) {
      return this.active === 'musicTracks' && field.key === 'audioUrl';
    },
    adminErrorMessage(error, fallback) {
      return error && error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : fallback;
    },
    statLabel(key) {
      return {
        quotes: '一句话',
        siteLinks: '网站入口',
        socialLinks: '社交链接',
        musicTracks: '音乐数量',
        photos: '照片数量'
      }[key] || key;
    },
    logout() {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminName');
      this.$router.replace('/admin/login');
    }
  }
};
</script>
